import { BasicAuthServiceHelper } from '@/modules/Auth'
import { DiscussionsTypes } from '@/modules/Discussions'
// import APIRoutes from './APIRoutes'
import { AppDispatch } from '@/store'
import { flattenHydraCollection, IRI } from 'emt-library'
import { ConversationSummary } from '@/dataV3/Conversation/ConversationSummary'
import { Conversation } from '@/dataV3/Conversation/Conversation'
import { Conversations, Messages } from './apiTypes'

/* *******************************
  GET DISCUSSIONS
******************************* */

export const dispatchGetDiscussionsAsync =
  (dispatch: AppDispatch) =>
  async (nextPageIRI?: IRI): Promise<ConversationSummary[]> => {
    const route = nextPageIRI ? Conversations.Get.NextPageRoute(nextPageIRI) : Conversations.Get.Route

    const rawResponse = await BasicAuthServiceHelper.requestAuthenticatedV2Async<Conversations.Get.Response>(route)
    const response = flattenHydraCollection(rawResponse)

    if (!nextPageIRI) {
      try {
        // No pageUrlPath, it means this is the firstPage, so we need to refresh unreadCount
        // TODO: get unread total count
      } catch (err) {
        console.warn('Cannot refresh unread count', err)
      }
    }

    dispatch({
      type: DiscussionsTypes.DiscussionsUpdated,
      payload: {
        isFirstPage: !nextPageIRI,
        discussionsCount: response.totalItems,
        discussions: response.member,
        nextPage: response.nextPage,
        mercureIRI: rawResponse['@topic'],
      },
    })

    return response.member
  }

/* *******************************
  DISCUSSION DETAILS
******************************* */

export const dispatchGetDiscussionAsync =
  (dispatch: AppDispatch) =>
  async (conversationIRI: IRI): Promise<Conversation> => {
    const response =
      await BasicAuthServiceHelper.requestAuthenticatedV2Async<Conversations.ConversationId.Get.Response>(
        Conversations.ConversationId.Get.Route(conversationIRI),
      )

    if (response) {
      // TODO: get unread total count

      dispatch({ type: DiscussionsTypes.DiscussionUpdated, payload: { conversation: response } })
    }

    return response
  }

export const dispatchGetDiscussionMessagesAsync =
  (dispatch: AppDispatch) =>
  async (nextPageIRI: IRI): Promise<Messages.Get.Response> => {
    const response = await BasicAuthServiceHelper.requestAuthenticatedV2Async<Messages.Get.Response>(
      Messages.Get.Route(nextPageIRI),
    )

    dispatch({
      type: DiscussionsTypes.DiscussionMessagesUpdated,
      payload: {
        discussionUuid: response.uuid,
        ...response.conversationItems,
      },
    })

    return response
  }

/* *******************************
  CREATE DISCUSSION / POST MESSAGE
******************************* */

export const dispatchCreateDiscussionAsync =
  (dispatch: AppDispatch) =>
  async (contactIRI: IRI, message: string): Promise<Conversation> => {
    const response = await BasicAuthServiceHelper.requestAuthenticatedV2Async<
      Conversations.Create.Response,
      Conversations.Create.Body
    >(Conversations.Create.Route(contactIRI), {
      body: { content: message },
    })

    dispatch({ type: DiscussionsTypes.DiscussionUpdated, payload: { conversation: response } })

    await dispatchGetDiscussionsAsync(dispatch)()

    return response
  }

export const dispatchAnswerDiscussionAsync =
  (dispatch: AppDispatch) =>
  async (conversationIRI: IRI, messageIRI: IRI, message: string): Promise<void> => {
    await BasicAuthServiceHelper.requestAuthenticatedV2Async<
      Conversations.ConversationId.SendMessage.Response,
      Conversations.ConversationId.SendMessage.Body
    >(Conversations.ConversationId.SendMessage.Route(messageIRI), {
      body: { content: message },
    })

    await dispatchGetDiscussionAsync(dispatch)(conversationIRI)
  }

/* *******************************
  DELETE DISCUSSION
******************************* */

export const dispatchDeleteDiscussionAsync =
  (dispatch: AppDispatch) =>
  async (unsubscribeIRI: IRI): Promise<void> => {
    const response =
      await BasicAuthServiceHelper.requestAuthenticatedV2Async<Conversations.ConversationId.Unsubscribe.Response>(
        Conversations.ConversationId.Unsubscribe.Route(unsubscribeIRI),
      )

    // TODO: get unread total count

    dispatch({ type: DiscussionsTypes.DiscussionDeleted, payload: response.uuid })
  }
